<template lang="pug">
feathers-vuex-get(service="dashboard" :id="sequence" :query="{}" :fetchQuery="queryParameter" watch="fetchQuery")
    div(slot-scope="{ item: DashboardDesserts, isGetPending }")
        h2 Dashboard
        v-divider
        div(v-if="hasCards(DashboardDesserts)" style="padding-top: 10px; font-size: 16px; font-weight: bold;")
            center Report for {{ view(DashboardDesserts).label }} (UTC)
        div(v-if="isGetPending")
            v-progress-linear(color="primary" :size="70" :width="7" indeterminate)
        br
        v-row(v-if="!isGetPending && hasCards(DashboardDesserts)")
            v-col(v-for="card in cardDefs" :key="card.key" cols="12" sm="12" md="6" lg="3" xl="3")
                v-card(:color="card.color" dark)
                    .d-flex.flex-no-wrap.justify-space-between
                        div
                            v-card-text {{ card.title }}
                                br
                                h1 {{ view(DashboardDesserts).cards[card.key].value | number }}
                            v-card-actions
                                v-icon mdi-menu-{{ arrow(view(DashboardDesserts).cards[card.key]) }}
                                b {{ view(DashboardDesserts).cards[card.key].pctChange | pct }}
                                | &nbsp;{{ view(DashboardDesserts).compare[card.key] }}

                        v-avatar.ma-3(tile)
                            v-icon(size="45") {{ card.icon }}

        v-alert(v-if="!isGetPending && DashboardDesserts && !hasCards(DashboardDesserts)" type="warning" text)
            | The API has not been updated for the date-filtered cards yet.

        br(v-if="!isGetPending && hasCards(DashboardDesserts)")
        v-row(v-if="!isGetPending && DashboardDesserts")
            v-col(v-if="hasCards(DashboardDesserts)" cols="12" sm="12" md="12" lg="8" xl="8")
                v-card
                    v-card-title
                        v-icon(small) mdi-home-analytics
                        .text-overline(style="flex: 1;") &nbsp;Analytics · {{ view(DashboardDesserts).chartLabel }}
                        //- The one period control: drives the cards and the chart.
                        v-btn-toggle.mr-2(v-model="period" mandatory dense color="primary")
                            v-btn(small value="day") Today
                            v-btn(small value="week") This week
                            v-btn(small value="month") This month
                            v-btn(small value="custom") Custom
                        date-range-picker.mr-2(
                            v-if="period === 'custom'"
                            v-model="customRange"
                            style="font-size: 13px; border-bottom: 1px solid #25252575; max-width: 200px;"
                        )
                        v-btn(small text @click="exportCsv(view(DashboardDesserts))" title="Export CSV")
                            v-icon(small left) mdi-download
                            | CSV
                    v-divider
                    line-chart(:chart-data="chartData(view(DashboardDesserts).series)" :options="chartOptions" style="height: 357px;")
            v-col(cols="12" sm="12" md="12" lg="4" xl="4")
                v-card
                    v-card-title
                        v-icon(small) mdi-account-multiple
                        .text-overline &nbsp;New Users
                    v-divider
                    v-list(dense)
                        template(v-for="(item, index) in reverseData(DashboardDesserts.newUsersLists)")
                            //- v-subheader Test
                            v-divider(v-if="index !== 0")
                            v-list-item
                                v-list-item-avatar
                                    v-img(:src="item.profile_picture")
                                v-list-item-content
                                    v-list-item-title {{ item.name }}
                                    v-list-item-subtitle {{ item.email }}
                                v-list-item-action
                                    v-chip(v-if="item.status === 1" small color="green" dark) {{ item.status | status }}
                                    v-chip(v-else small color="grey" dark) {{ item.status | status }}

        v-row(v-if="!isGetPending && DashboardDesserts")
            v-col(cols="12")
                v-card
                    v-card-title
                        v-icon(small) mdi-account-multiple
                        .text-overline &nbsp;Label Campaign
                        v-spacer
                        v-btn(color="primary" dark @click="$router.push('/label-campaign-manager')")
                            v-icon(small left) mdi-pencil
                            | Manage
                    v-divider
                    v-simple-table
                        template(v-slot:default)
                            thead
                                tr
                                    th Song name
                                    th Date Added
                            tbody
                                tr(v-for="(item, index) in DashboardDesserts.labelCampaign")
                                    td
                                        v-avatar(size="40" tile)
                                            v-img(:src="item.music_cover_thumb")
                                        | &nbsp;&nbsp;&nbsp;
                                        a(:href="`https://www.breakinghits.app/${item.user.result.url}`" :target="`__user-${item.id}`" v-html="item.title")
                                    td
                                        v-chip(small color="green" outlined) {{ item.date_added | date }}

</template>

<script>
import LineChart from './components/LineChart';
import moment from 'moment';
import { windowFor, buildView, granularityForRange } from './period-view';

// Card order, colours and icons are unchanged from the original dashboard.
const CARD_DEFS = [
    { key: 'totalUsers', title: 'TOTAL USERS', color: '#2196F3', icon: 'mdi-account-box-multiple', lineColor: null },
    { key: 'newUsers', title: 'NEW USERS', color: '#1DE9B6', icon: 'mdi-account-box', lineColor: '#2196F3' },
    { key: 'songAdded', title: 'SONG ADDED', color: '#90A4AE', icon: 'mdi-music-note-plus', lineColor: '#607D8B' },
    { key: 'songRated', title: 'SONG RATED', color: '#00B8D4', icon: 'mdi-star', lineColor: '#00B8D4' }
];

export default {
    data() {
        return {
            cardDefs: CARD_DEFS,
            // 'day' | 'week' | 'month' = today / this week / this month; 'custom' = customRange.
            period: 'day',
            customRange: [],
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                tooltips: {
                    mode: 'index',
                    intersect: false
                },
                hover: {
                    mode: 'index',
                    intersect: false
                },
                elements: {
                    point:{
                        radius: 2
                    }
                },
                scales: {
                    yAxes: [{
                        ticks: {
                            maxTicksLimit: 6,
                            beginAtZero: true,
                            display: true,
                            precision: 0
                        },
                            gridLines: {
                            drawBorder: false,
                            showBorder: false,
                            display: true,
                        },
                    }],
                    xAxes: [{
                        gridLines: {
                            drawBorder: false,
                            showBorder: false,
                            display: true,
                        },
                        ticks: {
                            display: true,
                            maxTicksLimit: 8
                        }
                    }]
                }
            }
        };
    },
    filters: {
        number(v) {
            return Number(v || 0).toLocaleString();
        },
        pct(v) {
            if (v === null || v === undefined) return '—';
            return (v > 0 ? '+' : '') + v + '%';
        },
        status(v) {
            switch(v) {
                case 0: return 'Inactive';
                case 1: return 'Active';
            }
            return 'Unknown';
        },
        date(v) {
            return moment(v).format('lll');
        }
    },
    computed: {
        isCustom() {
            return this.period === 'custom' && this.customRange && this.customRange.length === 2;
        },
        queryParameter() {
            if (this.isCustom) {
                return { dateRanged: this.customRange, granularity: granularityForRange(this.customRange) };
            }
            // Today / this week / this month: one daily series over the chart window; see period-view.js.
            return { dateRanged: windowFor(this.period === 'custom' ? 'day' : this.period), granularity: 'day' };
        },
        sequence() {
            return parseInt(((new Date()) / 1000).toFixed(0));
        }
    },
    components: {
        LineChart
    },
    methods: {
        // Guards against a cached response from an older API build without cards.
        hasCards(d) {
            return !!(d && d.cards && d.period && d.series);
        },
        arrow(card) {
            if (card.pctChange === null) return card.value > 0 ? 'up' : 'right';
            return card.pctChange >= 0 ? 'up' : 'down';
        },
        periodLabel(p) {
            if (p.isDefault) return 'Today';
            const s = moment.utc(p.start), e = moment.utc(p.end);
            if (p.start === p.end) return s.format('D MMM YYYY');
            return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
        },
        compareLabel(card, p) {
            if (card.key === 'totalUsers') return p.isDefault ? 'growth today' : 'growth in period';
            if (p.isDefault) return 'vs yesterday';
            const s = moment.utc(p.previousStart), e = moment.utc(p.previousEnd);
            return p.previousStart === p.previousEnd ? `vs ${s.format('D MMM')}` : `vs ${s.format('D MMM')} – ${e.format('D MMM')}`;
        },
        // What the cards and chart show. A custom range uses the API's figures as-is;
        // otherwise today / this week / this month are derived in period-view.js.
        view(d) {
            if (this.isCustom) {
                const p = d.period;
                return {
                    label: this.periodLabel(p),
                    cards: d.cards,
                    compare: CARD_DEFS.reduce((o, c) => ({ ...o, [c.key]: this.compareLabel(c, p) }), {}),
                    chartLabel: `trend: ${this.periodLabel(p)}, by ${d.series.granularity}`,
                    series: d.series,
                    csvPeriod: `${p.start} to ${p.end} vs ${p.previousStart} to ${p.previousEnd}`
                };
            }
            return buildView(d, this.period === 'custom' ? 'day' : this.period);
        },
        bucketLabel(bucket, granularity) {
            const d = moment.utc(bucket, 'YYYY-MM-DD');
            if (granularity === 'month') return d.format('MMM YYYY');
            if (granularity === 'week') return 'Wk ' + d.format('D MMM');
            return d.format('D MMM');
        },
        // Memoised so unrelated re-renders don't restart the chart animation.
        chartData(series) {
            const key = JSON.stringify(series);
            if (this._chartKey === key) return this._chartData;
            this._chartKey = key;
            this._chartData = {
                labels: series.points.map(p => this.bucketLabel(p.bucket, series.granularity)),
                datasets: CARD_DEFS.filter(c => c.lineColor).map(c => ({
                    label: c.title.charAt(0) + c.title.slice(1).toLowerCase(),
                    borderColor: c.lineColor,
                    backgroundColor: c.lineColor,
                    fill: false,
                    data: series.points.map(p => p[c.key]),
                    tension: 0.1
                }))
            };
            return this._chartData;
        },
        exportCsv(v) {
            const esc = x => {
                const s = x === null || x === undefined ? '' : String(x);
                return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
            };
            const rows = [
                ['Metric', 'Period', 'Value', 'Previous', '% change'],
                ...CARD_DEFS.map(c => [c.title, v.csvPeriod, v.cards[c.key].value, v.cards[c.key].previous, v.cards[c.key].pctChange]),
                [],
                [`Bucket start (${v.series.granularity}, UTC)`, 'New users', 'Song added', 'Song rated'],
                ...v.series.points.map(pt => [pt.bucket, pt.newUsers, pt.songAdded, pt.songRated])
            ];
            const csv = rows.map(r => r.map(esc).join(',')).join('\r\n');
            const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `dashboard_${v.series.start}_${v.series.end}_${v.series.granularity}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },
        reverseData(data) {
            return [...data].reverse();
        }
    }
}
</script>
